import { createTranslator, localizeUrl, normalizeLocale } from '../i18n/index.mjs'

export const downloadLinks = Object.freeze({
  ios: 'https://apps.apple.com/app/id6742995719',
  android: 'https://play.google.com/store/apps/details?id=happy.pet.com.ai'
})

const assessmentBaseUrl = 'https://www.starapp.fun/happypet/mind-scope/'

const toolDefinitions = Object.freeze([
  { id: 'tarot', emoji: '🔮', url: 'https://www.starapp.fun/happypet/ai-tarot/', glow: 'rgba(255, 139, 167, 0.32)', accent: '#ff8ba7' },
  { id: 'mbti', emoji: '🧠', url: 'https://www.starapp.fun/happypet/ai-mbti/', glow: 'rgba(163, 196, 243, 0.34)', accent: '#7aa9e8' },
  { id: 'chef', emoji: '🧊', url: 'https://www.starapp.fun/happypet/ai-chef/', glow: 'rgba(166, 216, 177, 0.35)', accent: '#79b88b' },
  { id: 'fortune', emoji: '☀️', url: 'https://www.starapp.fun/happypet/ai-fortune/', glow: 'rgba(253, 228, 207, 0.55)', accent: '#f2a65a' },
  { id: 'calorie', emoji: '🥗', url: 'https://www.starapp.fun/happypet/ai-calorie/', glow: 'rgba(179, 234, 211, 0.42)', accent: '#3fa77c' },
  { id: 'dream', emoji: '🌙', url: 'https://www.starapp.fun/happypet/ai-dream/', glow: 'rgba(181, 181, 255, 0.38)', accent: '#6d72d8' }
])

const assessmentDefinitions = Object.freeze([
  {
    id: 'mbti',
    icon: '🧠',
    color: '#8768df',
    url: `${assessmentBaseUrl}#/tests/mbti`
  },
  {
    id: 'sbti',
    icon: '🎭',
    color: '#e77990',
    url: `${assessmentBaseUrl}#/tests/sbti`
  },
  {
    id: 'iq',
    icon: '🧩',
    color: '#568ed9',
    url: `${assessmentBaseUrl}#/tests/iq`
  },
  {
    id: 'holland',
    icon: '🧭',
    color: '#3cb37e',
    url: `${assessmentBaseUrl}#/tests/holland`
  },
  {
    id: 'attachment',
    icon: '💞',
    color: '#d66f7e',
    url: `${assessmentBaseUrl}#/tests/attachment`
  }
])

const guidanceDefinitions = Object.freeze([
  { id: 'tarot', icon: '🔮', url: 'https://www.starapp.fun/happypet/ai-tarot/' },
  { id: 'fortune', icon: '☀️', url: 'https://www.starapp.fun/happypet/ai-fortune/' },
  { id: 'dream', icon: '🌙', url: 'https://www.starapp.fun/happypet/ai-dream/' }
])

const featureDefinitions = Object.freeze([
  { id: 'raise', emoji: '🐾', border: '#ffc1cc' },
  { id: 'hatch', emoji: '🥚', border: '#a3c4f3' },
  { id: 'coraising', emoji: '🥰', border: '#fde4cf' },
  { id: 'status', emoji: '📊', border: '#e1bee7' },
  { id: 'frequency', emoji: '🗓️', border: '#c8e6c9' },
  { id: 'cleaner', emoji: '🧹', border: '#ffe0b2' },
  { id: 'skills', emoji: '✨', border: '#d1c4e9' },
  { id: 'widget', emoji: '📱', border: '#b2ebf2' }
])

const stepDefinitions = Object.freeze([
  { id: 'adopt', num: '01' },
  { id: 'grow', num: '02' },
  { id: 'together', num: '03' }
])

const reviewDefinitions = Object.freeze([
  { id: 'one', name: 'Sarah K.', avatar: '🍥' },
  { id: 'two', name: 'Jason W.', avatar: '🍙' },
  { id: 'three', name: 'Emma L.', avatar: '🎀' }
])

export function buildHomeContent(locale) {
  const normalizedLocale = normalizeLocale(locale) || 'zh'
  const t = createTranslator(normalizedLocale)
  const localized = (url) => localizeUrl(url, normalizedLocale)

  const tools = toolDefinitions.map((tool) => ({
    ...tool,
    tag: t(`tool.${tool.id}.tag`),
    title: t(`tool.${tool.id}.title`),
    description: t(`tool.${tool.id}.description`),
    url: localized(tool.url)
  }))
  const assessments = assessmentDefinitions.map((assessment) => ({
    ...assessment,
    eyebrow: t(`assessment.${assessment.id}.eyebrow`),
    title: t(`assessment.${assessment.id}.title`),
    description: t(`assessment.${assessment.id}.description`),
    meta: t(`assessment.${assessment.id}.meta`),
    url: localized(assessment.url)
  }))
  const guidance = guidanceDefinitions.map((item) => ({
    ...item,
    title: t(`guidance.${item.id}.title`),
    description: t(`guidance.${item.id}.description`),
    note: t(`guidance.${item.id}.note`),
    url: localized(item.url)
  }))

  return {
    locale: normalizedLocale,
    brand: t('common.brand.name'),
    languageLabel: t('common.language.label'),
    nav: {
      skills: t('nav.skills.label'), tests: t('nav.tests.label'), guidance: t('nav.guidance.label'),
      features: t('nav.features.label'), experience: t('nav.experience.label'), community: t('nav.community.label'),
      news: t('nav.news.label'),
      download: t('common.download.action')
    },
    stores: {
      applePrefix: t('common.store.appleprefix'), appleName: t('common.store.applename'),
      googlePrefix: t('common.store.googleprefix'), googleName: t('common.store.googlename')
    },
    hero: { titleLead: t('home.hero.titlelead'), titleAccent: t('home.hero.titleaccent'), subtitle: t('home.hero.subtitle') },
    tools: { eyebrow: t('home.tools.eyebrow'), title: t('home.tools.title'), description: t('home.tools.description'), action: t('home.tools.action'), items: tools },
    assessments: { eyebrow: t('home.assessments.eyebrow'), title: t('home.assessments.title'), description: t('home.assessments.description'), action: t('home.assessments.action'), disclaimer: t('home.assessments.disclaimer'), items: assessments },
    guidance: { eyebrow: t('home.guidance.eyebrow'), title: t('home.guidance.title'), description: t('home.guidance.description'), items: guidance },
    features: { eyebrow: t('home.features.eyebrow'), title: t('home.features.title'), items: featureDefinitions.map((item) => ({ ...item, title: t(`feature.${item.id}.title`), description: t(`feature.${item.id}.description`) })) },
    steps: { eyebrow: t('home.steps.eyebrow'), title: t('home.steps.title'), items: stepDefinitions.map((item) => ({ ...item, title: t(`step.${item.id}.title`), desc: t(`step.${item.id}.description`) })) },
    reviews: { eyebrow: t('home.reviews.eyebrow'), title: t('home.reviews.title'), role: t('home.reviews.role'), items: reviewDefinitions.map((item) => ({ ...item, text: t(`review.${item.id}.text`) })) },
    news: {
      eyebrow: t('home.news.eyebrow'), title: t('home.news.title'), description: t('home.news.description'),
      readArticle: t('home.news.readarticle'), exploreAll: t('home.news.exploreall')
    },
    footer: {
      tagline: t('home.footer.tagline'), copyright: t('home.footer.copyright'),
      columns: [
        { title: t('home.footer.product'), links: [{ text: t('home.footer.skills'), url: '#tools' }, { text: t('home.footer.features'), url: '#features' }, { text: t('home.footer.widgets'), url: '#features' }] },
        { title: t('home.footer.support'), links: [{ text: t('home.footer.help'), url: `help.html?lang=${normalizedLocale}` }, { text: t('home.footer.privacy'), url: `privacy.html?lang=${normalizedLocale}` }, { text: t('home.footer.harmonyprivacy'), url: `privacy-harmonyos.html?lang=${normalizedLocale}` }, { text: t('home.footer.terms'), url: `user-agreement.html?lang=${normalizedLocale}` }] },
        {
          title: t('home.footer.social'),
          links: [
            { text: 'X', url: 'https://x.com/HappyPetApp', icon: 'M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932 6.064-6.932zm-1.292 19.494h2.039L6.486 3.24H4.298l13.311 17.407z' },
            { text: 'Discord', url: 'https://discord.gg/4WqnFFSZWY', icon: 'M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.006 14.006 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.894.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.927 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.01c.12.098.246.196.373.291a.077.077 0 0 1-.006.127 12.254 12.254 0 0 1-1.874.894.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.06.06 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z' }
          ]
        }
      ]
    },
    images: { logoAlt: t('home.image.logoalt'), screenAlt: t('home.image.screenalt'), enlargedAlt: t('home.image.enlargedalt'), close: t('home.image.close') }
  }
}

const englishHomeContent = buildHomeContent('en')

export const assessmentCards = Object.freeze(englishHomeContent.assessments.items)
export const mysticalGuidanceCards = Object.freeze(englishHomeContent.guidance.items)
